import { useQuery } from 'react-query'
import { BASE_API_URL } from 'constants/env'
import { API_METHODS } from 'constants/api-methods'
import { apiHeaders } from 'utils/api-headers'
import { store } from 'index'
import { handleErrors } from 'utils/handle-errors'
import { PaginatedAnsibleResponse, Ansible } from 'interfaces/ansible'

interface UseAvailableAnsiblesParams {
  terminalId: string
  requiredMacMinis?: number
  requiredPhones?: number
  enabled?: boolean
}

interface AvailableAnsiblesResponse {
  availableAnsibles: Ansible[]
  totalAnsibles: number
  isLoading: boolean
  error: Error | null
}

const fetchAvailableAnsibles = async ({
  terminalId,
  requiredMacMinis = 0,
  requiredPhones = 0,
}: UseAvailableAnsiblesParams): Promise<Ansible[]> => {
  const params = new URLSearchParams()
  params.append('terminalId', terminalId)
  params.append('status', 'ACTIVE') // Only show active ansibles
  params.append('include', 'terminal,stats') // Include capacity info
  params.append('limit', '100') // Get all ansibles for this terminal

  // Calculate total required slots for filtering
  const totalRequired = requiredMacMinis + requiredPhones
  if (totalRequired > 0) {
    params.append('minAvailableSlots', totalRequired.toString())
  }

  let response = await fetch(
    `${BASE_API_URL}/api/v4/phone_number_service/ansibles?${params.toString()}`,
    {
      method: API_METHODS.GET,
      headers: apiHeaders(store.getState()),
    }
  )

  response = handleErrors(response)
  const data: PaginatedAnsibleResponse = await response.json()

  // Client-side filtering for more precise capacity checking
  const filteredAnsibles = data.data.filter((ansible) => {
    const availableMacMinis = ansible.maxMacMinis - ansible.currentMacMinis
    const availablePhones =
      ansible.maxPhysicalPhones - ansible.currentPhysicalPhones

    return (
      availableMacMinis >= requiredMacMinis && availablePhones >= requiredPhones
    )
  })

  return filteredAnsibles
}

const useAvailableAnsibles = ({
  terminalId,
  requiredMacMinis = 0,
  requiredPhones = 0,
  enabled = true,
}: UseAvailableAnsiblesParams): AvailableAnsiblesResponse => {
  const {
    data: availableAnsibles = [],
    isLoading,
    error,
  } = useQuery(
    ['available-ansibles', terminalId, requiredMacMinis, requiredPhones],
    () =>
      fetchAvailableAnsibles({ terminalId, requiredMacMinis, requiredPhones }),
    {
      enabled:
        enabled && !!terminalId && (requiredMacMinis > 0 || requiredPhones > 0),
      staleTime: 30000, // 30 seconds
      retry: 2,
    }
  )

  return {
    availableAnsibles,
    totalAnsibles: availableAnsibles.length,
    isLoading,
    error: error as Error | null,
  }
}

export default useAvailableAnsibles
